import { useCallback, useMemo, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiBriefcase,
  FiFileText,
  FiMail,
  FiMapPin,
  FiPhone,
  FiTarget,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import { useToast } from "../../hooks/toast";
import { assertivaService } from "../../services/assertiva";
import { DetailModal } from "./components/DetailModal";
import { ScoreGauge } from "./components/ScoreGauge";
import { TopFilterBar } from "./components/TopFilterBar";
import { restrictiveAnalysisTokens } from "./tokens";
import { AnalysisData, DebtType } from "./types";

type OpenModalState =
  | { type: "none" }
  | { type: "empresas" }
  | { type: "dividas"; debtType: Exclude<DebtType, "registroDebitos"> }
  | { type: "registroDebitos" };

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function validateCPF(cpf: string): boolean {
  const normalized = onlyDigits(cpf);
  if (normalized.length !== 11 || /^(\d)\1+$/.test(normalized)) return false;

  const calculateDigit = (base: string, factor: number) => {
    let total = 0;
    for (let i = 0; i < base.length; i += 1) {
      total += Number(base[i]) * (factor - i);
    }
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const firstDigit = calculateDigit(normalized.slice(0, 9), 10);
  const secondDigit = calculateDigit(normalized.slice(0, 10), 11);

  return firstDigit === Number(normalized[9]) && secondDigit === Number(normalized[10]);
}

function validateCNPJ(cnpj: string): boolean {
  const normalized = onlyDigits(cnpj);
  if (normalized.length !== 14 || /^(\d)\1+$/.test(normalized)) return false;

  const calc = (base: string, factors: number[]) => {
    const total = base.split("").reduce((sum, num, index) => sum + Number(num) * factors[index], 0);
    const rest = total % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const base = normalized.slice(0, 12);
  const firstDigit = calc(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const secondDigit = calc(`${base}${firstDigit}`, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return firstDigit === Number(normalized[12]) && secondDigit === Number(normalized[13]);
}

/**
 * Aplica máscara dinâmica baseada no tamanho
 * CPF: 000.000.000-00 | CNPJ: 00.000.000/0000-00
 */
function applyDocumentMask(value: string): string {
  const digits = onlyDigits(value);
  
  if (digits.length <= 11) {
    // CPF
    return digits
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2");
  } else {
    // CNPJ
    return digits
      .slice(0, 14)
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
}

function currencyBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const debtLabels: Record<Exclude<DebtType, "registroDebitos">, string> = {
  acoesJudiciais: "Acoes Judiciais",
  protestos: "Protestos",
  cheques: "Cheques",
};

export default function RestrictiveAnalysis() {
  const { addToast } = useToast();
  const { brandBlue, brandOrange, pageBackground } = restrictiveAnalysisTokens.colors;
  const clickLockRef = useRef(false);

  const [documentValue, setDocumentValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [searched, setSearched] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<OpenModalState>({ type: "none" });

  const maskedDocument = useMemo(
    () => applyDocumentMask(documentValue),
    [documentValue]
  );

  const handleConsult = useCallback(async () => {
    // Anti-double-click: trava rigorosa
    if (clickLockRef.current) {
      console.warn("[ASSERTIVA] Consulta já em progresso. Clique duplicado bloqueado.");
      return;
    }

    const digits = onlyDigits(documentValue);
    
    // Detecta tipo de documento
    const isValidCPF = digits.length === 11 && validateCPF(digits);
    const isValidCNPJ = digits.length === 14 && validateCNPJ(digits);

    // Validação de documento
    if (!isValidCPF && !isValidCNPJ) {
      setAnalysis(null);
      setSearched(true);
      setInlineError("CPF/CNPJ inválido. Insira 11 dígitos (CPF) ou 14 dígitos (CNPJ).");
      addToast({
        title: "Documento inválido",
        description: "Não foi possível realizar a consulta com o documento informado.",
        type: "error",
      });
      return;
    }

    // Ativa trava de clique duplo + desabilita inputs
    clickLockRef.current = true;
    setLoading(true);
    setInlineError(null);

    try {
      // Consulta histórico primeiro (sem custo)
      const historicData = await assertivaService.obterHistorico(digits);
      if (historicData) {
        // Dados encontrados no histórico
        setAnalysis(historicData);
        setSearched(true);
        addToast({
          title: "Consulta Recuperada",
          description: "Dados recuperados do histórico (sem custo adicional).",
          type: "success",
        });
        return;
      }

      // Não encontrou no histórico, dispara consulta tarifada
      const result = await assertivaService.consultarAnalise(digits);
      setAnalysis(result);
      setSearched(true);
    } catch (error: any) {
      setAnalysis(null);
      setSearched(true);

      // Tratamento de erros específicos
      let errorTitle = "Falha na consulta";
      let errorDescription = "Ocorreu um erro ao consultar a análise restritiva.";

      if (error?.response?.status === 400) {
        const errorData = error.response.data;
        
        if (errorData?.message?.includes("Documento Inválido")) {
          errorTitle = "Documento Inválido";
          errorDescription = "O documento informado não é válido para consulta.";
        } else if (errorData?.message?.includes("Saldo Insuficiente")) {
          errorTitle = "Saldo Insuficiente";
          errorDescription = "Não há saldo suficiente na conta para realizar a consulta.";
        }
      } else if (error?.response?.status === 403) {
        errorTitle = "Acesso Negado";
        errorDescription = "Você não tem permissão para realizar esta consulta.";
      } else if (error?.response?.status === 429) {
        errorTitle = "Limite de Requisições";
        errorDescription = "Muitas requisições. Aguarde alguns minutos antes de tentar novamente.";
      } else if (typeof error?.message === "string" && error.message.trim().length > 0) {
        errorTitle = "Resposta inválida do backend";
        errorDescription = error.message;
      }

      setInlineError(errorDescription);
      addToast({
        title: errorTitle,
        description: errorDescription,
        type: "error",
      });
    } finally {
      // Sempre reseta o estado de loading
      setLoading(false);
      // Remove trava de clique duplo
      clickLockRef.current = false;
    }
  }, [documentValue, addToast]);

  const debtEvents = useMemo(() => {
    if (!analysis || openModal.type !== "dividas") return [];
    return analysis.eventosDividas
      .filter((item) => item.tipo === openModal.debtType)
      .sort((a, b) => (a.dataEvento < b.dataEvento ? 1 : -1));
  }, [analysis, openModal]);

  return (
    <Layout>
      <div className="min-h-screen px-4 py-6 md:px-8" style={{ backgroundColor: pageBackground }}>
        <div className="mx-auto w-full max-w-7xl space-y-5">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: brandBlue }}>Análise restritiva</h1>
            <p className="mt-1 text-sm text-slate-600">Faça a análise de crédito utilizando CPF ou CNPJ.</p>
          </div>

          <TopFilterBar
            documentValue={maskedDocument}
            loading={loading}
            onDocumentValueChange={setDocumentValue}
            onSubmit={handleConsult}
          />

          {inlineError && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="mt-0.5 h-4 w-4" />
                <div>
                  <p className="font-semibold">Falha na consulta</p>
                  <p>{inlineError}</p>
                </div>
              </div>
            </div>
          )}

          {searched && !analysis && !loading && !inlineError && (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-600 shadow-sm">
              Nenhum registro encontrado para este documento
            </div>
          )}

          {analysis && (
            <>
              <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-7">
                  <h2 className="mb-4 text-base font-semibold" style={{ color: brandBlue }}>Identificacao</h2>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Nome completo</p>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>{analysis?.nomeCompleto || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Documento</p>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>{analysis?.documento || "N/A"}</p>
                    </div>
                    {typeof analysis?.idade === "number" && analysis.idade > 0 && (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Idade</p>
                        <p className="text-sm font-semibold" style={{ color: brandBlue }}>{analysis.idade} anos</p>
                      </div>
                    )}
                    {analysis?.dataNascimento && analysis.dataNascimento !== "N/A" && analysis.dataNascimento !== "Não disponível" && (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Data de nascimento</p>
                        <p className="text-sm font-semibold" style={{ color: brandBlue }}>{analysis.dataNascimento}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Situacao na Receita Federal</p>
                      <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        {analysis?.situacaoReceita || "N/A"}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Data da consulta</p>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>{analysis?.dataConsulta || "N/A"}</p>
                    </div>
                  </div>

                  <hr className="my-4 border-slate-200" />

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Endereco</p>
                      <p className="flex items-start gap-2 text-sm text-slate-700">
                        <FiMapPin className="mt-0.5 h-4 w-4" style={{ color: brandBlue }} />
                        <span>
                          {analysis?.endereco &&
                          [
                            analysis.endereco.logradouro,
                            analysis.endereco.numero,
                            analysis.endereco.bairro,
                            analysis.endereco.cidade,
                            analysis.endereco.uf,
                            analysis.endereco.cep,
                          ].some((part) => Boolean(part && String(part).trim())) ? (
                            <>
                              {[analysis.endereco.logradouro, analysis.endereco.numero]
                                .filter((part) => Boolean(part && String(part).trim()))
                                .join(", ")}
                              {analysis.endereco.bairro ? ` - ${analysis.endereco.bairro}` : ""}
                              {analysis.endereco.cidade ? `, ${analysis.endereco.cidade}` : ""}
                              {analysis.endereco.uf ? ` - ${analysis.endereco.uf}` : ""}
                              {analysis.endereco.cep ? `, ${analysis.endereco.cep}` : ""}
                            </>
                          ) : (
                            <span className="text-slate-500">Não informado</span>
                          )}
                        </span>
                      </p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Contatos</p>
                      <div className="space-y-1 text-sm text-slate-700">
                        {analysis?.emails && analysis.emails.length > 0 ? (
                          analysis.emails.map((email) => (
                            <p key={email} className="flex items-center gap-2">
                              <FiMail className="h-4 w-4" style={{ color: brandBlue }} />
                              <span>{email}</span>
                            </p>
                          ))
                        ) : (
                          <p className="text-slate-500 text-xs">Sem e-mails registrados</p>
                        )}
                        {analysis?.telefones && analysis.telefones.length > 0 ? (
                          analysis.telefones.map((phone) => (
                            <p key={phone} className="flex items-center gap-2">
                              <FiPhone className="h-4 w-4" style={{ color: brandBlue }} />
                              <span>{phone}</span>
                            </p>
                          ))
                        ) : (
                          <p className="text-slate-500 text-xs">Sem telefones registrados</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <ScoreGauge
                    score={analysis?.score || 0}
                    riskLabel={analysis?.classeRisco || "N/A"}
                    riskDescription={analysis?.descricaoRisco || "Não informado"}
                  />
                </div>
              </section>

              <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <FiBriefcase className="h-4 w-4" style={{ color: brandBlue }} />
                    <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: brandBlue }}>
                      Participacoes em Empresas
                    </h3>
                  </div>

                  <div className="space-y-2">
                    {analysis?.participacoesEmpresas && analysis.participacoesEmpresas.length > 0 ? (
                      analysis.participacoesEmpresas.map((empresa) => (
                        <div key={empresa.id} className="rounded-lg bg-slate-50 p-2">
                          <p className="text-sm font-semibold" style={{ color: brandBlue }}>{empresa.nomeFantasia}</p>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              empresa.status === "Ativa"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-700"
                            }`}
                          >
                            {empresa.status}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500">Nenhuma participação registrada</p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setOpenModal({ type: "empresas" })}
                    className="mt-3 text-sm font-semibold transition hover:opacity-80"
                    style={{ color: brandOrange }}
                  >
                    + mais detalhes
                  </button>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <FiTarget className="h-4 w-4" style={{ color: brandBlue }} />
                    <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: brandBlue }}>Renda Presumida</h3>
                  </div>
                  <p className="text-3xl font-bold" style={{ color: brandBlue }}>{currencyBRL(analysis?.rendaPresumida || 0)}</p>
                  <p className="mt-2 text-xs text-slate-500">Estimativa de capacidade financeira mensal.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <FiFileText className="h-4 w-4" style={{ color: brandBlue }} />
                    <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: brandBlue }}>
                      Registro de Consultas
                    </h3>
                  </div>
                  <p className="text-3xl font-bold" style={{ color: brandBlue }}>{analysis?.consultasUltimos90Dias || 0}</p>
                  <p className="mt-2 text-xs text-slate-500">Consultas no documento nos ultimos 90 dias.</p>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide" style={{ color: brandBlue }}>DIVIDAS</h3>

                <div className="divide-y divide-slate-200">
                  <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>Acoes Judiciais</p>
                      <p className="text-lg font-bold text-slate-900">{currencyBRL(analysis?.dividas?.acoesJudiciaisTotal || 0)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenModal({ type: "dividas", debtType: "acoesJudiciais" })}
                      className="text-left text-sm font-semibold hover:opacity-80 md:text-right"
                      style={{ color: brandOrange }}
                    >
                      + mais detalhes
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>Protestos</p>
                      <p className="text-lg font-bold text-slate-900">{currencyBRL(analysis?.dividas?.protestosTotal || 0)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenModal({ type: "dividas", debtType: "protestos" })}
                      className="text-left text-sm font-semibold hover:opacity-80 md:text-right"
                      style={{ color: brandOrange }}
                    >
                      + mais detalhes
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>Cheques</p>
                      <p className="text-lg font-bold text-slate-900">
                        {analysis?.dividas?.chequesQuantidade || 0} registro(s) - {currencyBRL(analysis?.dividas?.chequesTotal || 0)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenModal({ type: "dividas", debtType: "cheques" })}
                      className="text-left text-sm font-semibold hover:opacity-80 md:text-right"
                      style={{ color: brandOrange }}
                    >
                      + mais detalhes
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: brandBlue }}>Registro de Debitos</p>
                      <p className="text-lg font-bold text-slate-900">{currencyBRL(analysis?.dividas?.registroDebitosTotal || 0)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenModal({ type: "registroDebitos" })}
                      className="text-left text-sm font-semibold hover:opacity-80 md:text-right"
                      style={{ color: brandOrange }}
                    >
                      + mais detalhes
                    </button>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <DetailModal
        open={Boolean(analysis && openModal.type === "empresas")}
        title="Participacoes em Empresas"
        onClose={() => setOpenModal({ type: "none" })}
      >
        {analysis && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse text-left">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <th className="border-b border-slate-200 px-3 py-2">CNPJ</th>
                  <th className="border-b border-slate-200 px-3 py-2">Razao Social</th>
                  <th className="border-b border-slate-200 px-3 py-2">Data de Entrada</th>
                  <th className="border-b border-slate-200 px-3 py-2">CNAE</th>
                  <th className="border-b border-slate-200 px-3 py-2">E-mail</th>
                </tr>
              </thead>
              <tbody>
                {analysis?.participacoesEmpresas && analysis.participacoesEmpresas.map((empresa) => (
                  <tr key={empresa.id} className="text-sm text-slate-700">
                    <td className="border-b border-slate-100 px-3 py-2">{empresa.cnpj}</td>
                    <td className="border-b border-slate-100 px-3 py-2">{empresa.razaoSocial}</td>
                    <td className="border-b border-slate-100 px-3 py-2">{empresa.dataEntrada}</td>
                    <td className="border-b border-slate-100 px-3 py-2">{empresa.cnae}</td>
                    <td className="border-b border-slate-100 px-3 py-2">{empresa.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailModal>

      <DetailModal
        open={Boolean(analysis && openModal.type === "dividas")}
        title={openModal.type === "dividas" ? `${debtLabels[openModal.debtType]} - Detalhes` : "Detalhes"}
        onClose={() => setOpenModal({ type: "none" })}
      >
        <div className="space-y-3">
          {debtEvents.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              Nao ha eventos registrados para esta categoria.
            </p>
          )}

          {debtEvents.map((event) => (
            <article key={event.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                  <span className="font-semibold" style={{ color: brandBlue }}>Data do Evento:</span> {event.dataEvento}
                </p>
                <p>
                  <span className="font-semibold" style={{ color: brandBlue }}>Valor da Divida:</span> {currencyBRL(event.valor)}
                </p>
                <p className="md:col-span-2">
                  <span className="font-semibold" style={{ color: brandBlue }}>Descricao do Titulo:</span> {event.descricaoTitulo}
                </p>
                <p>
                  <span className="font-semibold" style={{ color: brandBlue }}>Origem:</span> {event.origem}
                </p>
                <p>
                  <span className="font-semibold" style={{ color: brandBlue }}>Natureza:</span> {event.natureza}
                </p>
              </div>
            </article>
          ))}
        </div>
      </DetailModal>

      <DetailModal
        open={Boolean(analysis && openModal.type === "registroDebitos")}
        title="Registro de Debitos - Detalhamento"
        onClose={() => setOpenModal({ type: "none" })}
      >
        {analysis && (
          <div className="space-y-4">
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">Valor consolidado</p>
              <p className="text-2xl font-bold text-orange-900">{currencyBRL(analysis?.dividas?.registroDebitosTotal || 0)}</p>
            </div>

            <div className="space-y-2">
              {analysis?.dividas?.registroDebitosDetalhes && analysis.dividas.registroDebitosDetalhes.length > 0 ? (
                analysis.dividas.registroDebitosDetalhes.map((registro, index) => (
                  <article key={`${registro.credor}-${index}`} className="rounded-xl border border-slate-200 p-4">
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold" style={{ color: brandBlue }}>Data de inclusao:</span> {registro.dataInclusao}
                    </p>
                    <p className="mt-1 text-sm text-slate-700">
                      <span className="font-semibold" style={{ color: brandBlue }}>Credor:</span> {registro.credor}
                    </p>
                    <p className="mt-1 text-sm text-slate-700">
                      <span className="font-semibold" style={{ color: brandBlue }}>Descricao:</span> {registro.descricao}
                    </p>
                  </article>
                ))
              ) : (
                <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  Nenhum registro de débito encontrado.
                </p>
              )}
            </div>
          </div>
        )}
      </DetailModal>
    </Layout>
  );
}
